// Frontend/src/pages/hoa-admin/Screens/AnnouncementsPage.tsx
//
// The announcements tab, rebuilt on the M1 kit.
//
// This page always called the real API and always looked finished. It never
// worked: the backend scoped reads by an organization_id that almost no
// building has, and the compose form read the target building from
// sessionStorage("condo_id") — a key nothing in this app has ever written. So
// every create posted an empty building into a branch that raised a builtin
// PermissionError and left the view as a 500.
//
// With the backend fixed (M2 A1/A2), the visible work is: pick which of your
// buildings this is for, and say honestly how many people that reaches.

import React from "react"
import { useQuery } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  Calendar,
  Check,
  Megaphone,
  Pencil,
  Pin,
  Plus,
  Trash2,
  Users,
} from "lucide-react"

import {
  useAnnouncements,
  useAnnouncement,
  useAnnouncementReach,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
} from "@/api/announcements"
import { listBuildings } from "@/api/buildings"
import ResponsiveModal from "@/components/common/ResponsiveModal"
import {
  ConfirmDialog,
  FormField,
  FormFooter,
  FormPanel,
  areaClass,
  fieldClass,
} from "@/components/payments/PaymentBits"
import { useLanguage } from "@/contexts/LanguageContext"
import {
  ANNOUNCEMENT_PRIORITY_ICON,
  ANNOUNCEMENT_PRIORITY_TONE,
  ANNOUNCEMENT_STATE_TONE,
  announcementPriorityIsLoud,
  announcementState,
} from "@/lib/announcementStyles"
import { BRAND } from "@/lib/issueStyles"
import type {
  Announcement,
  AnnouncementListItem,
  AnnouncementPriority,
  UpdateAnnouncementData,
} from "@/types/announcement"

const PRIORITIES: AnnouncementPriority[] = ["low", "medium", "high", "urgent"]

type BuildingChoice = { id: string; name: string; code: string }

type ComposeForm = {
  title: string
  content: string
  priority: AnnouncementPriority
  expires_at: string
  is_pinned: boolean
  building_ids: string[]
}

const EMPTY_FORM: ComposeForm = {
  title: "",
  content: "",
  priority: "medium",
  expires_at: "",
  is_pinned: false,
  building_ids: [],
}

function errorMessage(
  error: unknown,
  t: (key: string, params?: Record<string, string | number>) => string,
  fallbackKey: string
): string {
  const err = error as {
    code?: string
    message?: string
    response?: { status?: number; data?: Record<string, unknown> }
  }

  if (err?.code === "ECONNABORTED" || /timeout/i.test(String(err?.message ?? ""))) {
    return t("error.timeout")
  }
  if (!err?.response) return t("error.network")

  // The serializer names the building it rejected. Surfacing that beats a
  // generic "forbidden" for an admin who mistargeted one of four buildings.
  const detail = err.response.data?.building_ids
  if (Array.isArray(detail) && typeof detail[0] === "string") return detail[0]

  const status = Number(err.response.status ?? 0)
  if (status === 401) return t("error.unauthorized")
  if (status === 403) return t("error.forbidden")
  if (status === 404) return t("error.notFound")
  if (status >= 500) return t("error.serverError")

  return t(fallbackKey)
}

/** `/buildings/` is already scoped to `hoa_admin = user`. */
function useMyBuildings() {
  return useQuery({
    queryKey: ["buildings", "mine"],
    queryFn: listBuildings,
    staleTime: 5 * 60 * 1000,
    select: (rows): BuildingChoice[] =>
      rows.map((b) => ({ id: b.id, name: b.name, code: b.code })),
  })
}

/* ================================================================== */

export default function AnnouncementsPage() {
  const { t, language } = useLanguage()
  const dateLocale = language === "es" ? "es-ES" : "en-US"

  const formatDate = React.useCallback(
    (value: string) => new Intl.DateTimeFormat(dateLocale).format(new Date(value)),
    [dateLocale]
  )

  const { data, isLoading } = useAnnouncements()
  const { data: buildings } = useMyBuildings()

  const createMutation = useCreateAnnouncement()
  const updateMutation = useUpdateAnnouncement()
  const deleteMutation = useDeleteAnnouncement()

  const [composing, setComposing] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = React.useState<AnnouncementListItem | null>(null)
  const [form, setForm] = React.useState<ComposeForm>(EMPTY_FORM)

  // Default to every building the admin owns. Announcing to the whole
  // community is the normal case; a subset is the exception.
  React.useEffect(() => {
    if (composing && buildings && form.building_ids.length === 0) {
      setForm((f) => ({ ...f, building_ids: buildings.map((b) => b.id) }))
    }
  }, [composing, buildings, form.building_ids.length])

  const items = data ?? []

  function openCompose() {
    setForm({ ...EMPTY_FORM, building_ids: buildings?.map((b) => b.id) ?? [] })
    setComposing(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.title.trim() || !form.content.trim() || form.building_ids.length === 0) return

    createMutation.mutate(
      {
        title: form.title.trim(),
        content: form.content.trim(),
        priority: form.priority,
        building_ids: form.building_ids,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
        is_pinned: form.is_pinned,
      },
      {
        onSuccess: () => {
          toast.success(t("hoa.announcements.toast.created"))
          setForm(EMPTY_FORM)
          setComposing(false)
        },
        onError: (error) =>
          toast.error(errorMessage(error, t, "hoa.announcements.toast.createFailed")),
      }
    )
  }

  return (
    <div className="relative space-y-5">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      <div className="flex items-center justify-between rounded-2xl bg-white/80 p-3 ring-1 ring-black/5 backdrop-blur sm:p-4">
        <p className="text-sm text-gray-600">
          {isLoading
            ? t("common.loading")
            : t("hoa.announcements.count", { count: items.length })}
        </p>
        <button
          type="button"
          onClick={openCompose}
          className="inline-flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition active:scale-[0.99]"
          style={{ background: "linear-gradient(100deg,#1F3A93 0%,#1F3A93 55%,#27AE60 160%)" }}
        >
          <Plus className="h-4 w-4" />
          {t("hoa.announcements.new")}
        </button>
      </div>

      {isLoading && <ListSkeleton />}

      {!isLoading && items.length === 0 && (
        <div className="rounded-2xl bg-white/80 py-14 text-center ring-1 ring-black/5">
          <Megaphone className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t("hoa.announcements.empty")}</p>
          <p className="mt-1 text-[13px] text-gray-500">{t("hoa.announcements.emptyHint")}</p>
        </div>
      )}

      <div className="space-y-3">
        {items.map((a) => (
          <AnnouncementRow
            key={a.id}
            item={a}
            formatDate={formatDate}
            onEdit={() => setEditingId(a.id)}
            onDelete={() => setConfirmDelete(a)}
          />
        ))}
      </div>

      <ComposeModal
        open={composing}
        form={form}
        setForm={setForm}
        buildings={buildings ?? []}
        busy={createMutation.isPending}
        onSubmit={handleSubmit}
        onClose={() => setComposing(false)}
      />

      {editingId && (
        <EditModal
          announcementId={editingId}
          buildings={buildings ?? []}
          saving={updateMutation.isPending}
          onClose={() => setEditingId(null)}
          onSave={(payload: UpdateAnnouncementData) =>
            updateMutation.mutate(
              { id: editingId, data: payload },
              {
                onSuccess: () => {
                  toast.success(t("hoa.announcements.toast.updated"))
                  setEditingId(null)
                },
                onError: (error) =>
                  toast.error(errorMessage(error, t, "hoa.announcements.toast.updateFailed")),
              }
            )
          }
        />
      )}

      <ConfirmDialog
        open={confirmDelete !== null}
        title={t("hoa.announcements.deleteTitle")}
        message={t("hoa.announcements.deleteMessage", { title: confirmDelete?.title ?? "" })}
        detail={t("hoa.announcements.deleteDetail")}
        confirmLabel={t("common.delete")}
        busy={deleteMutation.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (!confirmDelete) return
          deleteMutation.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast.success(t("hoa.announcements.toast.deleted"))
              setConfirmDelete(null)
            },
            onError: (error) =>
              toast.error(errorMessage(error, t, "hoa.announcements.toast.deleteFailed")),
          })
        }}
      />
    </div>
  )
}

/* ================================================================== */

function AnnouncementRow({
  item,
  formatDate,
  onEdit,
  onDelete,
}: {
  item: AnnouncementListItem
  formatDate: (value: string) => string
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useLanguage()
  const tone = ANNOUNCEMENT_PRIORITY_TONE[item.priority]
  const Icon = ANNOUNCEMENT_PRIORITY_ICON[item.priority]
  const state = announcementState(item)
  const stateTone = ANNOUNCEMENT_STATE_TONE[state]

  return (
    <article className="relative overflow-hidden rounded-2xl bg-white p-4 ring-1 ring-black/5">
      <span className="absolute inset-y-0 left-0 w-1" style={{ background: tone.rail }} />

      <div className="flex items-start justify-between gap-3 pl-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {item.is_pinned && (
              <Pin
                className="h-4 w-4 shrink-0 text-amber-600"
                aria-label={t("hoa.announcements.pinned")}
              />
            )}
            <h3 className="truncate text-[15px] font-semibold text-gray-900">{item.title}</h3>
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1"
              style={{
                background: announcementPriorityIsLoud(item.priority) ? tone.rail : tone.bg,
                color: announcementPriorityIsLoud(item.priority) ? "#fff" : tone.fg,
                borderColor: tone.ring,
                boxShadow: `inset 0 0 0 1px ${tone.ring}`,
              }}
            >
              <Icon className="h-3 w-3" />
              {t(`common.priority.${item.priority}`)}
            </span>

            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{
                background: stateTone.bg,
                color: stateTone.fg,
                boxShadow: `inset 0 0 0 1px ${stateTone.ring}`,
              }}
            >
              {t(`hoa.announcements.state.${state}`)}
            </span>

            {/* Which buildings this went to. The whole point of M2's model
                change, and invisible everywhere else on the page. */}
            {item.buildings.map((b) => (
              <span
                key={b.id}
                className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600"
              >
                {b.code || b.name}
              </span>
            ))}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-gray-500">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {formatDate(item.published_at)}
            </span>
            <span>
              {t("hoa.announcements.postedBy", {
                name: item.author_name || t("common.unknown"),
              })}
            </span>
            {item.expires_at && (
              <span>{t("hoa.announcements.expiresOn", { date: formatDate(item.expires_at) })}</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onEdit}
            title={t("common.edit")}
            className="grid h-9 w-9 place-items-center rounded-xl text-gray-500 ring-1 ring-gray-200 transition hover:bg-gray-50 hover:text-gray-900"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            title={t("common.delete")}
            className="grid h-9 w-9 place-items-center rounded-xl text-gray-500 ring-1 ring-gray-200 transition hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </article>
  )
}

/* ================================================================== */

/**
 * Which buildings this announcement is for.
 *
 * Rendered even when the admin owns one building: without it the page never
 * says where an announcement went, and "all my buildings" is invisible
 * agreement rather than a stated choice.
 */
function BuildingPicker({
  buildings,
  selected,
  onChange,
  error,
}: {
  buildings: BuildingChoice[]
  selected: string[]
  onChange: (ids: string[]) => void
  error?: string
}) {
  const { t } = useLanguage()
  const allSelected = buildings.length > 0 && selected.length === buildings.length

  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id])
  }

  return (
    <div className="space-y-2">
      {buildings.length > 1 && (
        <button
          type="button"
          onClick={() => onChange(allSelected ? [] : buildings.map((b) => b.id))}
          className="text-[12px] font-medium underline-offset-2 hover:underline"
          style={{ color: BRAND }}
        >
          {allSelected ? t("hoa.announcements.selectNone") : t("hoa.announcements.selectAll")}
        </button>
      )}

      <div className="flex flex-wrap gap-2">
        {buildings.map((b) => {
          const on = selected.includes(b.id)
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => toggle(b.id)}
              aria-pressed={on}
              className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[13px] font-medium transition ${
                on
                  ? "text-white shadow-sm"
                  : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
              }`}
              style={on ? { background: BRAND } : undefined}
            >
              {on && <Check className="h-3.5 w-3.5" />}
              {b.code || b.name}
            </button>
          )
        })}
      </div>
      {error && <p className="text-[12px] font-medium text-red-600">{error}</p>}
    </div>
  )
}

/**
 * The expiry field, with an explicit clear button.
 *
 * The native `datetime-local` widget's own clear affordance is inconsistent
 * across browsers (Firefox has none at all), so once a date is picked there
 * was no reliable way back to "no expiry." This button is unconditional and
 * only rendered while a value is set.
 */
function ExpiryField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useLanguage()
  return (
    <div className="flex items-center gap-2">
      <input
        type="datetime-local"
        className={`${fieldClass} flex-1`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="h-11 shrink-0 rounded-xl px-3 text-[13px] font-medium text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50"
        >
          {t("hoa.announcements.expiresClear")}
        </button>
      )}
    </div>
  )
}

/**
 * How many people this actually reaches, before it is sent.
 *
 * Two numbers rather than one because that is the truth: 78% of production
 * residents never claimed their unit, so they have an email address and no
 * account to receive an in-app notification on.
 */
function ReachLine({ buildingIds }: { buildingIds: string[] }) {
  const { t } = useLanguage()
  const { data, isLoading } = useAnnouncementReach(buildingIds)

  if (buildingIds.length === 0) {
    return (
      <p className="text-[12px] text-gray-500">{t("hoa.announcements.reach.noBuildings")}</p>
    )
  }
  if (isLoading || !data) {
    return <p className="text-[12px] text-gray-400">{t("hoa.announcements.reach.counting")}</p>
  }

  return (
    <div className="flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2.5">
      <Users className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
      <p className="text-[12px] leading-relaxed text-gray-600">
        <span className="font-semibold text-gray-900">
          {t("hoa.announcements.reach.summary", {
            inApp: data.in_app,
            emailOnly: data.email_only,
          })}
        </span>
        {data.email_only > 0 && (
          <span className="mt-0.5 block text-gray-500">
            {t("hoa.announcements.reach.emailOnlyHint")}
          </span>
        )}
      </p>
    </div>
  )
}

/* ================================================================== */

function ComposeModal({
  open,
  form,
  setForm,
  buildings,
  busy,
  onSubmit,
  onClose,
}: {
  open: boolean
  form: ComposeForm
  setForm: React.Dispatch<React.SetStateAction<ComposeForm>>
  buildings: BuildingChoice[]
  busy: boolean
  onSubmit: (e: React.FormEvent) => void
  onClose: () => void
}) {
  const { t } = useLanguage()
  const [touched, setTouched] = React.useState({ title: false, content: false, buildings: false })
  const touch = (field: keyof typeof touched) =>
    setTouched((tt) => ({ ...tt, [field]: true }))

  const titleValid = form.title.trim().length >= 3
  const contentValid = form.content.trim().length >= 10
  const buildingsValid = form.building_ids.length > 0
  const canSend = titleValid && contentValid && buildingsValid

  // The first unmet requirement, shown next to the submit button regardless
  // of touched state — a disabled button with no reason is the thing this
  // issue exists to fix.
  const disabledReason = !titleValid
    ? t("hoa.announcements.error.titleTooShort")
    : !contentValid
      ? t("hoa.announcements.error.contentTooShort")
      : !buildingsValid
        ? t("hoa.announcements.error.noBuildings")
        : undefined

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={t("hoa.announcements.createNew")}
      subtitle={t("hoa.announcements.createSubtitle")}
      headerAccent={BRAND}
      footer={
        <div className="space-y-2.5">
          <ReachLine buildingIds={form.building_ids} />
          <FormFooter
            form="announcement-compose"
            onCancel={onClose}
            cancelLabel={t("common.cancel")}
            submitLabel={t("hoa.announcements.publish")}
            busyLabel={t("hoa.announcements.publishing")}
            busy={busy}
            disabled={!canSend}
            disabledReason={disabledReason}
          />
        </div>
      }
    >
      <form id="announcement-compose" onSubmit={onSubmit} className="space-y-3.5">
        <FormPanel title={t("hoa.announcements.section.message")}>
          <FormField
            label={t("common.title")}
            required
            error={touched.title && !titleValid ? t("hoa.announcements.error.titleTooShort") : undefined}
          >
            <input
              type="text"
              className={fieldClass}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              onBlur={() => touch("title")}
              placeholder={t("hoa.announcements.titlePlaceholder")}
              required
              minLength={3}
              maxLength={200}
            />
          </FormField>

          <FormField
            label={t("common.content")}
            required
            error={
              touched.content && !contentValid ? t("hoa.announcements.error.contentTooShort") : undefined
            }
          >
            <textarea
              className={`${areaClass} min-h-28`}
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              onBlur={() => touch("content")}
              placeholder={t("hoa.announcements.contentPlaceholder")}
              required
              minLength={10}
            />
          </FormField>
        </FormPanel>

        <FormPanel
          title={t("hoa.announcements.section.audience")}
          hint={t("hoa.announcements.section.audienceHint")}
        >
          <BuildingPicker
            buildings={buildings}
            selected={form.building_ids}
            onChange={(ids) => {
              touch("buildings")
              setForm((f) => ({ ...f, building_ids: ids }))
            }}
            error={touched.buildings && !buildingsValid ? t("hoa.announcements.error.noBuildings") : undefined}
          />
        </FormPanel>

        <FormPanel title={t("hoa.announcements.section.options")}>
          <FormField label={t("common.priority")}>
            <select
              className={fieldClass}
              value={form.priority}
              onChange={(e) =>
                setForm((f) => ({ ...f, priority: e.target.value as AnnouncementPriority }))
              }
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`common.priority.${p}`)}
                </option>
              ))}
            </select>
          </FormField>

          {/* No publish-date picker. published_at accepts a future date and
              the resident queryset hides those rows, but this project has no
              task queue, so nothing would fire the notification when the date
              arrived. Offering the control would promise scheduling the
              system cannot do. */}
          <FormField
            label={t("hoa.announcements.expiresOptional")}
            hint={t("hoa.announcements.expiresHint")}
          >
            <ExpiryField
              value={form.expires_at}
              onChange={(v) => setForm((f) => ({ ...f, expires_at: v }))}
            />
          </FormField>

          <label className="flex items-center gap-2.5">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={form.is_pinned}
              onChange={(e) => setForm((f) => ({ ...f, is_pinned: e.target.checked }))}
            />
            <span className="text-[13px] text-gray-700">{t("hoa.announcements.pinToTop")}</span>
          </label>
        </FormPanel>
      </form>
    </ResponsiveModal>
  )
}

/* ================================================================== */

function EditModal({
  announcementId,
  buildings,
  saving,
  onClose,
  onSave,
}: {
  announcementId: string
  buildings: BuildingChoice[]
  saving: boolean
  onClose: () => void
  onSave: (data: UpdateAnnouncementData) => void
}) {
  const { t } = useLanguage()
  const { data: announcement, isLoading } = useAnnouncement(announcementId)

  if (isLoading || !announcement) {
    return (
      <ResponsiveModal
        open
        onClose={onClose}
        title={t("hoa.announcements.editTitle")}
        headerAccent={BRAND}
      >
        <p className="py-8 text-center text-sm text-gray-500">{t("common.loading")}</p>
      </ResponsiveModal>
    )
  }

  return (
    <EditForm
      item={announcement}
      buildings={buildings}
      saving={saving}
      onClose={onClose}
      onSave={onSave}
    />
  )
}

function EditForm({
  item,
  buildings,
  saving,
  onClose,
  onSave,
}: {
  item: Announcement
  buildings: BuildingChoice[]
  saving: boolean
  onClose: () => void
  onSave: (data: UpdateAnnouncementData) => void
}) {
  const { t } = useLanguage()
  const [title, setTitle] = React.useState(item.title)
  const [content, setContent] = React.useState(item.content)
  const [priority, setPriority] = React.useState<AnnouncementPriority>(item.priority)
  const [isPinned, setIsPinned] = React.useState(item.is_pinned)
  const [buildingIds, setBuildingIds] = React.useState<string[]>(
    item.buildings.map((b) => b.id)
  )
  const [expiresAt, setExpiresAt] = React.useState(
    item.expires_at ? toLocalInput(item.expires_at) : ""
  )
  const [touched, setTouched] = React.useState({ title: false, content: false, buildings: false })
  const touch = (field: keyof typeof touched) =>
    setTouched((tt) => ({ ...tt, [field]: true }))

  const titleValid = title.trim().length >= 3
  const contentValid = content.trim().length >= 10
  const buildingsValid = buildingIds.length > 0

  const disabledReason = !titleValid
    ? t("hoa.announcements.error.titleTooShort")
    : !contentValid
      ? t("hoa.announcements.error.contentTooShort")
      : !buildingsValid
        ? t("hoa.announcements.error.noBuildings")
        : undefined

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!titleValid || !contentValid || !buildingsValid) return
    onSave({
      title: title.trim(),
      content: content.trim(),
      priority,
      building_ids: buildingIds,
      // null, not undefined: clearing the field must clear the column, and
      // undefined would be dropped from the PATCH body entirely.
      expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      is_pinned: isPinned,
    })
  }

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      title={t("hoa.announcements.editTitle")}
      subtitle={item.title}
      headerAccent={ANNOUNCEMENT_PRIORITY_TONE[priority].rail}
      footer={
        <FormFooter
          form="announcement-edit"
          onCancel={onClose}
          cancelLabel={t("common.cancel")}
          submitLabel={t("common.save")}
          busyLabel={t("common.working")}
          busy={saving}
          disabled={!titleValid || !contentValid || !buildingsValid}
          disabledReason={disabledReason}
        />
      }
    >
      <form id="announcement-edit" onSubmit={submit} className="space-y-3.5">
        <FormPanel title={t("hoa.announcements.section.message")}>
          <FormField
            label={t("common.title")}
            required
            error={touched.title && !titleValid ? t("hoa.announcements.error.titleTooShort") : undefined}
          >
            <input
              type="text"
              className={fieldClass}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => touch("title")}
              required
              minLength={3}
              maxLength={200}
            />
          </FormField>
          <FormField
            label={t("common.content")}
            required
            error={
              touched.content && !contentValid ? t("hoa.announcements.error.contentTooShort") : undefined
            }
          >
            <textarea
              className={`${areaClass} min-h-28`}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onBlur={() => touch("content")}
              required
              minLength={10}
            />
          </FormField>
        </FormPanel>

        <FormPanel
          title={t("hoa.announcements.section.audience")}
          hint={t("hoa.announcements.editAudienceHint")}
        >
          <BuildingPicker
            buildings={buildings}
            error={touched.buildings && !buildingsValid ? t("hoa.announcements.error.noBuildings") : undefined}
            selected={buildingIds}
            onChange={(ids) => {
              touch("buildings")
              setBuildingIds(ids)
            }}
          />
        </FormPanel>

        <FormPanel title={t("hoa.announcements.section.options")}>
          <FormField label={t("common.priority")}>
            <select
              className={fieldClass}
              value={priority}
              onChange={(e) => setPriority(e.target.value as AnnouncementPriority)}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`common.priority.${p}`)}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label={t("hoa.announcements.expiresOptional")} hint={t("hoa.announcements.expiresHint")}>
            <ExpiryField value={expiresAt} onChange={setExpiresAt} />
          </FormField>

          <label className="flex items-center gap-2.5">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
            />
            <span className="text-[13px] text-gray-700">{t("hoa.announcements.pinToTop")}</span>
          </label>
        </FormPanel>
      </form>
    </ResponsiveModal>
  )
}

/* ================================================================== */

/**
 * ISO string -> the value a `datetime-local` input expects.
 *
 * `toISOString().slice(0,16)` — what this page used before — renders UTC into
 * a control the browser reads as local time, so an 8pm expiry displayed as
 * midnight for a Caracas admin and saved back four hours off.
 */
function toLocalInput(iso: string): string {
  const d = new Date(iso)
  const offsetMs = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - offsetMs).toISOString().slice(0, 16)
}

function ListSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-24 animate-pulse rounded-2xl bg-white/70 ring-1 ring-black/5" />
      ))}
    </div>
  )
}
